'use client';

import Stack from '@mui/material/Stack';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SelectLanguage from '@/components/SelectLanguage';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { HeaderContentProps } from './types';
import { DRAWER_WIDTH, HEADER_HEIGHT } from './constants';
import useNavigationItems from './hooks/useNavigationItems';
import LoginButton from './components/LoginButton';
import DesktopSimpleLink from './components/DesktopSimpleLink';
import DesktopMenuButton from './components/DesktopMenuButton';
import DesktopDrawerContent from './components/DesktopDrawerContent';
import MobileNavigationList from './components/MobileNavigationList';
import useTranslations from '@/hooks/useTranslations';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import Paper from '@mui/material/Paper';
import Container from '@mui/material/Container';
import Portal from '@mui/material/Portal';

export default function HeaderContent({ navigation = [], menuState }: HeaderContentProps) {
  const { menuHeaders } = useNavigationItems(navigation);
  const { t } = useTranslations();

  return (
    <>
      {/* Desktop view */}
      <Stack
        direction='row'
        sx={{
          flexGrow: 1,
          color: 'text.primary',
          alignItems: 'center',
          display: { xs: 'none', md: 'flex' },
        }}
        spacing={1}
      >
        {navigation.map((navigationItem, index) => {
          if (
            navigationItem.visible &&
            navigationItem.href &&
            navigationItem._type === 'link' &&
            !navigationItem.hidden
          ) {
            return <DesktopSimpleLink key={`menu-${index}`} link={navigationItem} index={index} />;
          }

          if (navigationItem._type === 'menuHeader' && !navigationItem.hidden) {
            return (
              <DesktopMenuButton
                key={`menu-${index}`}
                menuHeader={navigationItem}
                onMenuClick={menuState.openDesktopMenu}
                onMenuEnter={menuState.openDesktopMenu}
                openMenuIndex={menuState.openMenuIndex}
                isMenuOpen={menuState.desktopMenuOpen}
                index={index}
              />
            );
          }
        })}

        <SelectLanguage showLabel={false} isMarketingWebsite />
        <LoginButton isMobile={false} onClose={menuState.closeDesktopMenu} />
      </Stack>

      <Portal>
        {/* Blur backdrop */}
        <Fade in={menuState.desktopMenuOpen} timeout={300} unmountOnExit>
          <Box
            sx={{
              position: 'fixed',
              top: HEADER_HEIGHT,
              left: 0,
              right: 0,
              bottom: 0,
              backdropFilter: 'blur(10px)',
              zIndex: (theme) => theme.zIndex.appBar - 2,
              pointerEvents: 'none',
            }}
          />
        </Fade>

        {/* Dropdown panel */}
        <Slide in={menuState.desktopMenuOpen} direction='down' timeout={300} unmountOnExit>
          <Paper
            elevation={3}
            onMouseEnter={menuState.cancelCloseTimer}
            onMouseLeave={menuState.scheduleCloseDesktopMenu}
            sx={{
              position: 'fixed',
              top: HEADER_HEIGHT,
              left: 0,
              right: 0,
              width: '100dvw',
              maxHeight: '90dvh',
              borderRadius: 0,
              zIndex: (theme) => theme.zIndex.appBar - 1,
              overflowY: 'auto',
            }}
          >
            <Container>
              <DesktopDrawerContent
                menuHeaders={menuHeaders}
                openMenuIndex={menuState.openMenuIndex}
                onClose={menuState.closeDesktopMenu}
              />
            </Container>
          </Paper>
        </Slide>
      </Portal>

      {/* Mobile view */}
      <Stack
        direction='row'
        sx={{
          flexGrow: 1,
          justifyContent: 'flex-end',
          alignItems: 'center',
          display: { xs: 'flex', md: 'none' },
        }}
        spacing={1}
      >
        <IconButton
          edge='end'
          color='inherit'
          aria-label='menu'
          onClick={menuState.toggleMobileMenu}
          sx={{ color: 'primary.main' }}
        >
          <MenuRoundedIcon />
        </IconButton>
      </Stack>

      <Drawer
        anchor='right'
        open={menuState.mobileMenuOpen}
        onClose={menuState.closeMobileMenu}
        slotProps={{
          paper: {
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default',
            },
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Stack direction='row' sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'primary.main' }}>
              {t('website.menu')}
            </Box>
            <IconButton
              edge='end'
              color='inherit'
              aria-label='close menu'
              onClick={menuState.closeMobileMenu}
              sx={{ color: 'primary.main' }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          <MobileNavigationList
            navigation={navigation}
            expandedMobileMenu={menuState.expandedMobileMenu}
            onClose={menuState.closeMobileMenu}
            onToggleSubmenu={menuState.toggleMobileSubmenu}
          />

          <Divider sx={{ mb: 1 }} />

          <Stack spacing={1} direction='column'>
            <SelectLanguage showLabel={true} />
            <LoginButton isMobile={true} onClose={menuState.closeMobileMenu} />
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}
